if (!customElements.get('vorug-craft-rail')) {
  customElements.define('vorug-craft-rail', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.controller = new AbortController();
      const {signal} = this.controller;
      const track = this.querySelector('.vce-track');
      const previous = this.querySelector('[data-previous]');
      const next = this.querySelector('[data-next]');
      const count = this.querySelector('[data-count]');
      const cards = Array.from(track.children);
      const controls = this.querySelector('.vce-controls');
      const update = () => {
        const step = cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
        const index = Math.round(track.scrollLeft / step);
        controls.hidden = track.scrollWidth <= track.clientWidth + 2;
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        count.textContent = `${Math.min(index + 1, cards.length)} / ${cards.length}`;
      };
      const move = direction => {
        const step = cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
        const target = Math.round(track.scrollLeft / step) + direction;
        track.scrollTo({left: Math.max(0, Math.min(target * step, track.scrollWidth - track.clientWidth)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      };
      previous.addEventListener('click', () => move(-1), {signal});
      next.addEventListener('click', () => move(1), {signal});
      track.addEventListener('scroll', update, {passive:true,signal});
      track.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, {signal});
      this.observer = new ResizeObserver(update);
      this.observer.observe(track);
      update();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.bound = false; }
  });
}

(() => {
  const cleanups = new WeakMap();
  const setup = (root = document) => root.querySelectorAll('.vce-products').forEach(track => {
    if (track.dataset.craftCarouselReady) return;
    track.dataset.craftCarouselReady = 'true';
    const cards = [...track.children];
    if (cards.length < 2) return;
    const mobile = matchMedia('(max-width: 600px)');
    const controls = document.createElement('div');
    controls.className = 'vce-carousel-controls';
    controls.innerHTML = '<span class="label_font_uppercase">STUDIO SELECTION <span class="vce-carousel-position" role="status" aria-live="polite" aria-atomic="true">01 / 03</span></span><div><button type="button" aria-label="Previous rug">←</button><button type="button" aria-label="Next rug">→</button></div>';
    track.after(controls);
    const label = track.getAttribute('aria-label');
    track.setAttribute('aria-label', 'Studio rug selection');
    const abort = new AbortController();
    const options = {signal: abort.signal};
    let copies = [], start = 0, period = 0, timer, frame;
    const position = controls.querySelector('.vce-carousel-position');
    const updatePosition = () => {
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (!step) return;
      const index = ((Math.round((track.scrollLeft - start) / step) % cards.length) + cards.length) % cards.length;
      position.textContent = `${String(index + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      position.setAttribute('aria-label', `Rug ${index + 1} of ${cards.length}`);
    };
    const jump = left => {
      track.style.scrollSnapType = 'none';
      track.scrollLeft = left;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { frame = null; track.style.scrollSnapType = ''; });
    };
    const measure = () => {
      if (!mobile.matches || !copies.length) return;
      const inset = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      start = cards[0].offsetLeft - inset;
      period = copies[cards.length].offsetLeft - cards[0].offsetLeft;
      jump(start); updatePosition();
    };
    const activate = () => {
      clearTimeout(timer);
      copies.forEach(card => card.remove()); copies = [];
      controls.hidden = !mobile.matches;
      if (!mobile.matches) { track.scrollLeft = 0; return; }
      const duplicate = card => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true'); clone.inert = true;
        clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        clone.removeAttribute('id'); clone.removeAttribute('data-shopify-editor-block');
        return clone;
      };
      const before = cards.map(duplicate), after = cards.map(duplicate);
      track.prepend(...before); track.append(...after); copies = [...before, ...after];
      measure();
    };
    track.addEventListener('scroll', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!mobile.matches || !period) return;
        const delta = track.scrollLeft - start;
        if (delta < -1 || delta >= period - 1) jump(start + ((delta % period) + period) % period);
        updatePosition();
      }, 160);
    }, {passive: true, signal: abort.signal});
    const move = direction => {
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      track.scrollBy({left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    };
    controls.querySelectorAll('button').forEach((button, i) => button.addEventListener('click', () => move(i ? 1 : -1), options));
    track.addEventListener('focusin', event => {
      if (mobile.matches && cards.some(card => card === event.target || card.contains(event.target))) {
        const card = cards.find(card => card === event.target || card.contains(event.target));
        jump(card.offsetLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0));
      }
    }, options);
    mobile.addEventListener('change', activate, options);
    let width = track.clientWidth;
    const resize = new ResizeObserver(() => {
      if (width === track.clientWidth) return;
      width = track.clientWidth; measure();
    });
    resize.observe(track);
    cleanups.set(track, () => {
      abort.abort();
      resize.disconnect();
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      track.style.scrollSnapType = '';
      copies.forEach(card => card.remove());
      controls.remove();
      if (label === null) track.removeAttribute('aria-label'); else track.setAttribute('aria-label', label);
      delete track.dataset.craftCarouselReady;
      cleanups.delete(track);
    });
    activate();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup()); else setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.vce-products').forEach(track => cleanups.get(track)?.());
  });
})();

(() => {
  if (window.vorugCraftMotion || !Element.prototype.animate || !window.IntersectionObserver) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const active = new Set();
  const jobs = new WeakMap();
  const animate = (element, frames, duration = 700, delay = 0) => {
    if (!element || reduced.matches) return;
    const animation = element.animate(frames, {duration, delay, easing: ease, fill: 'backwards'});
    active.add(animation);
    const release = () => { active.delete(animation); animation.cancel(); };
    animation.finished.then(release, () => active.delete(animation));
    return animation;
  };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      const job = jobs.get(entry.target);
      jobs.delete(entry.target);
      if (job && !reduced.matches) job();
    });
  }, {rootMargin: '0px 0px -8% 0px', threshold: 0.08});
  const enter = (element, job) => {
    if (!element || reduced.matches) return;
    // Deep-link arrivals keep already-passed chapters settled and readable.
    if (element.getBoundingClientRect().bottom < 0) return;
    jobs.set(element, job); observer.observe(element);
  };
  const words = heading => {
    if (!heading || heading.dataset.craftWords) return heading ? [...heading.querySelectorAll('.vce-word-inner')] : [];
    heading.dataset.craftWords = 'true';
    const text = heading.textContent;
    heading.setAttribute('aria-label', text.trim());
    const fragment = document.createDocumentFragment();
    text.split(/(\s+)/).forEach(part => {
      if (/^\s+$/.test(part)) { fragment.append(document.createTextNode(part)); return; }
      if (!part) return;
      const outer = document.createElement('span');
      outer.className = 'vce-word'; outer.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span'); inner.className = 'vce-word-inner'; inner.textContent = part;
      outer.append(inner); fragment.append(outer);
    });
    heading.replaceChildren(fragment);
    return [...heading.querySelectorAll('.vce-word-inner')];
  };
  const whenImageReady = (photo, job) => {
    const image = photo?.querySelector('img');
    if (!image) return;
    const reveal = () => {
      const bounds = photo.getBoundingClientRect();
      if (bounds.bottom > 0 && bounds.top < innerHeight && !reduced.matches) job();
    };
    if (image.complete && image.naturalWidth) reveal();
    else image.addEventListener('load', reveal, {once: true});
  };
  const imageArrival = (photo, direction, duration = 800) => {
    if (!photo) return;
    const crop = direction === 'side' ? 'inset(0 100% 0 0)' : 'inset(0 0 100% 0)';
    animate(photo, [{clipPath: crop}, {clipPath: 'inset(0 0 0 0)'}], duration);
    animate(photo.querySelector('img'), [{transform: 'scale(1.035)'}, {transform: 'scale(1)'}], duration + 250);
  };
  const setup = (root = document) => {
    root.querySelectorAll('.vce').forEach(section => {
      if (section.dataset.craftMotionReady) return;
      section.dataset.craftMotionReady = 'true';
      const title = words(section.querySelector('h1, h2'));
      enter(section.querySelector('h1, h2'), () => title.forEach((word, i) => animate(word,
        [{transform: 'translateY(110%)', opacity: 0}, {transform: 'translateY(0)', opacity: 1}],
        700, section.id === 'opening' ? 100 + Math.min(i * 45, 180) : Math.min(i * 45, 180))));
      if (section.id === 'opening') {
        const photo = section.querySelector('.vce-media');
        enter(photo, () => whenImageReady(photo, () => animate(photo.querySelector('img'),
          [{transform: 'scale(1.035)'}, {transform: 'scale(1)'}], 1200)));
      } else if (section.matches('.vce--split')) {
        const photo = section.querySelector('.vce-media');
        enter(photo, () => whenImageReady(photo, () => imageArrival(photo, section.id === 'material' ? 'down' : 'side')));
      } else if (section.matches('.vce--rail, .vce--products')) {
        const track = section.querySelector('.vce-track, .vce-products');
        enter(track, () => [...track.children].filter(card => !card.inert).forEach((card, i) => {
          animate(card, [{transform: 'translateY(24px)', opacity: .5}, {transform: 'translateY(0)', opacity: 1}], 650, Math.min(i * 80, 160));
          const photo = card.querySelector('.vce-media');
          whenImageReady(photo, () => animate(photo.querySelector('img'), [{transform: 'scale(1.025)'}, {transform: 'scale(1)'}], 950));
        }));
      }
    });
    root.querySelectorAll('details').forEach(disclosure => {
      if (disclosure.dataset.craftFeedbackReady) return;
      disclosure.dataset.craftFeedbackReady = 'true';
      disclosure.addEventListener('toggle', () => {
        if (disclosure.open) animate(disclosure.querySelector('nav, .vce-body'),
          [{opacity: .25, transform: 'translateY(8px)'}, {opacity: 1, transform: 'translateY(0)'}], 250);
      });
    });
  };
  document.addEventListener('focusin', event => {
    active.forEach(animation => { if (animation.effect?.target?.contains(event.target)) animation.cancel(); });
  });
  const settle = () => { if (reduced.matches) active.forEach(animation => animation.cancel()); };
  reduced.addEventListener('change', settle);
  // Content is visible without JS, between animations, and when motion is reduced.
  document.addEventListener('visibilitychange', () => { if (document.hidden) active.forEach(animation => animation.finish()); });
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    active.forEach(animation => { if (event.target.contains(animation.effect?.target)) animation.cancel(); });
    event.target.querySelectorAll('.vce, .vce-media, .vce-track, .vce-products').forEach(element => observer.unobserve(element));
  });
  window.vorugCraftMotion = {setup};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup()); else setup();
})();
